import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Calculator as CalcIcon, Plus, X } from "@phosphor-icons/react";

export default function Calculator() {
  const [services, setServices] = useState([]);
  const [wasteTypes, setWasteTypes] = useState([]);
  const [charges, setCharges] = useState([]);
  const [products, setProducts] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [wasteLines, setWasteLines] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [extraIds, setExtraIds] = useState([]);
  const [distance, setDistance] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/services"), api.get("/waste-types"),
      api.get("/charges"), api.get("/products"),
    ]).then(([s, w, c, p]) => {
      setServices(s.data); setWasteTypes(w.data);
      setCharges(c.data); setProducts(p.data);
    });
  }, []);

  const _newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID() : `k_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const addWasteLine = () => setWasteLines(w => [...w, { _key: _newKey(), waste_type_id: wasteTypes[0]?.id || "", quantity_kg: 1 }]);
  const addProductLine = () => setProductLines(p => [...p, { _key: _newKey(), product_id: products[0]?.id || "", quantity: 1 }]);
  const toggleExtra = (id) => setExtraIds(l => l.includes(id) ? l.filter(x => x !== id) : [...l, id]);

  const calc = async () => {
    try {
      const r = await api.post("/calculate-price", {
        service_type_id: serviceId || null,
        waste_lines: wasteLines.map(l => ({ waste_type_id: l.waste_type_id, quantity_kg: Number(l.quantity_kg) })),
        products: productLines.map(l => ({ product_id: l.product_id, quantity: Number(l.quantity) })),
        extra_charge_ids: extraIds,
        distance_km: Number(distance) || 0,
      });
      setResult(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Price Calculator" subtitle="Service & Pricing">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          {/* Service */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Step 1</div>
            <div className="font-heading text-lg mb-3">Service type</div>
            <select data-testid="calc-service" value={serviceId} onChange={(e) => setServiceId(e.target.value)}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
              <option value="">— none —</option>
              {services.map(s => <option key={s.id} value={s.id}>{s.name} (₹{s.base_charge})</option>)}
            </select>
          </div>

          {/* Waste */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Step 2</div>
                <div className="font-heading text-lg">Waste collected</div>
              </div>
              <button data-testid="calc-add-waste" onClick={addWasteLine}
                      className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                <Plus size={12} /> Add waste
              </button>
            </div>
            {wasteLines.length === 0 && <div className="text-sm text-[#8A8780]">No waste added.</div>}
            <div className="space-y-2">
              {wasteLines.map((l, idx) => (
                <div key={l._key || idx} className="flex gap-2">
                  <select value={l.waste_type_id}
                          onChange={(e) => setWasteLines(w => w.map((x, i) => i === idx ? { ...x, waste_type_id: e.target.value } : x))}
                          className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
                    {wasteTypes.map(w => <option key={w.id} value={w.id}>{w.waste_name} — ₹{w.rate_per_kg}/kg</option>)}
                  </select>
                  <input type="number" step="any" min="0" placeholder="kg" value={l.quantity_kg}
                         onChange={(e) => setWasteLines(w => w.map((x, i) => i === idx ? { ...x, quantity_kg: e.target.value } : x))}
                         className="w-28 h-10 px-3 border border-[#E5E2D9] rounded-md font-mono text-sm" />
                  <button onClick={() => setWasteLines(w => w.filter((_, i) => i !== idx))}
                          className="h-10 px-3 border border-[#E5E2D9] rounded-md text-[#C25D38]"><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Distance + extras */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Step 3</div>
            <div className="font-heading text-lg mb-3">Distance & extras</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Distance (km)</label>
                <input data-testid="calc-distance" type="number" step="any" min="0"
                       value={distance} onChange={(e) => setDistance(e.target.value)}
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono" />
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Extra charges</div>
              <div className="flex flex-wrap gap-2">
                {charges.map(c => {
                  const on = extraIds.includes(c.id);
                  return (
                    <button key={c.id} onClick={() => toggleExtra(c.id)}
                            className={`px-3 h-9 rounded-md text-xs border transition-colors ${on ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "border-[#E5E2D9] text-[#5C5A55] hover:bg-[#F7F6F2]"}`}>
                      {c.type} · ₹{c.amount}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Products */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Step 4 (optional)</div>
                <div className="font-heading text-lg">Products</div>
              </div>
              <button onClick={addProductLine} className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                <Plus size={12} />Add product
              </button>
            </div>
            {productLines.length === 0 && <div className="text-sm text-[#8A8780]">No products added.</div>}
            <div className="space-y-2">
              {productLines.map((l, idx) => (
                <div key={l._key || idx} className="flex gap-2">
                  <select value={l.product_id}
                          onChange={(e) => setProductLines(p => p.map((x, i) => i === idx ? { ...x, product_id: e.target.value } : x))}
                          className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
                    {products.map(p => <option key={p.id} value={p.id}>{p.name} — ₹{p.price}</option>)}
                  </select>
                  <input type="number" min="0" placeholder="qty" value={l.quantity}
                         onChange={(e) => setProductLines(p => p.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))}
                         className="w-24 h-10 px-3 border border-[#E5E2D9] rounded-md font-mono text-sm" />
                  <button onClick={() => setProductLines(p => p.filter((_, i) => i !== idx))}
                          className="h-10 px-3 border border-[#E5E2D9] rounded-md text-[#C25D38]"><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-[#1A3317] text-white rounded-md p-6 sticky top-24" data-testid="calc-result-panel">
            <div className="flex items-center gap-2 mb-3">
              <CalcIcon size={20} weight="fill" />
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">Server-computed total</div>
            </div>
            <div className="font-heading text-5xl tracking-tight" data-testid="calc-total">
              ₹{result ? result.total.toFixed(2) : "0.00"}
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-2">No manual override</div>
            <button onClick={calc} data-testid="calc-compute"
                    className="w-full mt-5 h-10 bg-[#C25D38] hover:bg-[#A74E2F] text-white rounded-md text-sm font-medium transition-colors">
              Calculate
            </button>
            {result && (
              <div className="mt-6 pt-6 border-t border-white/10 space-y-1.5 text-sm">
                {result.items.map((it, i) => (
                  <div key={i} className="flex justify-between gap-4">
                    <span className="text-white/80 truncate">{it.label}</span>
                    <span className="font-mono">₹{it.amount.toFixed(2)}</span>
                  </div>
                ))}
                <div className="pt-3 border-t border-white/10 text-xs text-white/60 grid grid-cols-2 gap-1 font-mono">
                  <div>Waste: ₹{result.waste}</div>
                  <div>Service: ₹{result.service}</div>
                  <div>Distance: ₹{result.distance}</div>
                  <div>Extras: ₹{result.extras}</div>
                  <div>Products: ₹{result.products}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
